import React, { useContext, useEffect, useMemo, useState } from "react";
import Moment from "moment";

import { LoginContext } from "../../context/loginContext";
import { SalesContext } from "../../context/salesContext";
import { CustomersContext } from "../../context/customersContext";
import { Badge, Card, EmptyState, PageHeader, Spinner, StatCard } from "../ui";

const STATUS_TONE = {
    "Pending Parts": "warning",
    "Pending Paint": "warning",
    Prep: "info",
    "Making Door": "info",
    "Ready For Installation": "success",
    "Ready For Pick Up": "success",
};

const renderPendingDays = (sale) => {
    const expected = Moment(sale.expectedCompletionDate).startOf("day");
    const today = Moment().startOf("day");
    const pending = expected.diff(today, "days");

    if (pending < 0) {
        return (
            <span className="text-xs font-semibold text-danger-600">
                {Math.abs(pending)} day{Math.abs(pending) === 1 ? "" : "s"} overdue
            </span>
        );
    }
    if (pending === 0) {
        return <span className="text-xs font-semibold text-info-600">Due today</span>;
    }
    if (pending < 4) {
        return <span className="text-xs font-semibold text-warning-700">Coming up soon</span>;
    }
    return (
        <span className="text-xs font-medium text-ink-500">
            {pending} day{pending === 1 ? "" : "s"} remaining
        </span>
    );
};

/** API / cookie user shapes vary; try common keys (email-only accounts, etc.). */
const USER_DISPLAY_KEYS = [
    "username",
    "userName",
    "email",
    "emailAddress",
    "mail",
    "name",
    "fullName",
    "displayName",
];

function firstNonEmptyField(obj, keys) {
    if (!obj || typeof obj !== "object") return "";
    for (const k of keys) {
        const v = obj[k];
        if (v != null && String(v).trim()) return String(v).trim();
    }
    return "";
}

function userObjectFromSession(raw) {
    if (raw == null) return null;
    if (typeof raw === "object" && !Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
        try {
            const o = JSON.parse(raw);
            if (o && typeof o === "object" && !Array.isArray(o)) return o;
        } catch {
            return null;
        }
    }
    return null;
}

/** First segment of Basic `user:password` (login field is often email). */
function loginIdFromEncodedCredentials(encoded) {
    if (typeof encoded !== "string" || !encoded.trim()) return "";
    const token = encoded.replace(/^Basic\s+/i, "").trim();
    try {
        const decoded = atob(token);
        const loginId = decoded.split(":")[0];
        return loginId?.trim() || "";
    } catch {
        try {
            const inner = JSON.parse(token);
            if (typeof inner === "string" && inner !== token) {
                return loginIdFromEncodedCredentials(inner);
            }
        } catch {
            /* ignore */
        }
        return "";
    }
}

function resolveSignedInDisplay(currentUserRaw, encodedCredentials) {
    const userObj = userObjectFromSession(currentUserRaw);
    if (userObj) {
        const nested = userObj.user && typeof userObj.user === "object" ? userObj.user : null;
        const fromNested = firstNonEmptyField(nested, USER_DISPLAY_KEYS);
        if (fromNested) return fromNested;
        const direct = firstNonEmptyField(userObj, USER_DISPLAY_KEYS);
        if (direct) return direct;
    }
    return loginIdFromEncodedCredentials(encodedCredentials);
}

const Dashboard = () => {
    const { currentUser, authenticatedUser: encodedCredentials } = useContext(LoginContext);
    const { action } = useContext(SalesContext);
    const { customers } = useContext(CustomersContext);

    const [stats, setStats] = useState();
    const [upcoming, setUpcoming] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const [statsData, upcomingData] = await Promise.all([
                action.getStats(),
                action.getUpcoming(5),
            ]);
            if (cancelled) return;

            const safeStats =
                statsData && typeof statsData === "object" && !Array.isArray(statsData)
                    ? {
                          overdue: statsData.overdue || 0,
                          ready: statsData.ready || 0,
                          doors: statsData.doors || 0,
                          pending: statsData.pending || 0,
                          total: statsData.total || 0,
                      }
                    : { overdue: 0, ready: 0, doors: 0, pending: 0, total: 0 };

            setStats(safeStats);
            setUpcoming(Array.isArray(upcomingData) ? upcomingData : []);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signedInDisplay = useMemo(
        () => resolveSignedInDisplay(currentUser, encodedCredentials),
        [currentUser, encodedCredentials],
    );

    const customerById = (id) => (customers || []).find((c) => c._id === id) || {};

    return (
        <div className="space-y-6">
            {signedInDisplay ? (
                <p className="text-base text-ink-600">
                    Welcome,{" "}
                    <span className="">{signedInDisplay}</span>
                </p>
            ) : null}
            <PageHeader
                eyebrow="Overview"
                title="Dashboard"
                description="A snapshot of work-order status and what's coming up next."
            />

            {loading || !stats ? (
                <div className="flex h-48 items-center justify-center rounded-xl border border-ink-100 bg-white">
                    <Spinner size="lg" />
                </div>
            ) : (
                <>
            <section
                aria-label="Work order overview"
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
            >
                <StatCard
                    tone="danger"
                    label="Overdue"
                    value={stats.overdue}
                    icon={<i className="fas fa-exclamation-circle" />}
                    hint="Past expected completion"
                />
                <StatCard
                    tone="success"
                    label="Ready"
                    value={stats.ready}
                    icon={<i className="fas fa-thumbs-up" />}
                    hint="Pickup or install"
                />
                <StatCard
                    tone="info"
                    label="Doors in progress"
                    value={stats.doors}
                    icon={<i className="fas fa-tasks" />}
                    hint="Prep + making"
                />
                <StatCard
                    tone="warning"
                    label="Pending"
                    value={stats.pending}
                    icon={<i className="fas fa-ellipsis-h" />}
                    hint="Awaiting parts/paint"
                />
            </section>

            <Card padded={false}>
                <div className="flex items-center justify-between border-b border-ink-100 px-4 py-4 sm:px-6">
                    <div>
                        <h2 className="text-base font-semibold text-ink-900">Coming up</h2>
                        <p className="text-xs text-ink-500">
                            Next jobs nearing expected completion
                        </p>
                    </div>
                </div>

                {upcoming.length === 0 ? (
                    <div className="p-4 sm:p-6">
                        <EmptyState
                            icon={<i className="far fa-calendar-check" />}
                            title="No upcoming jobs"
                            description="Nothing is scheduled for completion in the near future."
                        />
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden overflow-x-auto md:block">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-medium uppercase tracking-wider text-ink-500">
                                        <th className="px-6 py-3">Job</th>
                                        <th className="px-6 py-3">Expected completion</th>
                                        <th className="px-6 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-100">
                                    {upcoming.map((sale, index) => {
                                        const customer = customerById(sale.customerId);
                                        const status = sale.workOrder && sale.workOrder.status;
                                        return (
                                            <tr
                                                key={sale._id || index}
                                                className="hover:bg-ink-50/50"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-start gap-3">
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">
                                                            {index + 1}
                                                        </span>
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-ink-900">
                                                                {customer.firstName}{" "}
                                                                {customer.lastName}
                                                            </p>
                                                            <p className="text-xs text-ink-500">
                                                                {sale.product}
                                                            </p>
                                                            <p className="text-xs text-ink-500">
                                                                Ordered{" "}
                                                                {Moment(sale.orderDate).format(
                                                                    "ll",
                                                                )}{" "}
                                                                · {customer.city || "—"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm text-ink-900">
                                                        {Moment(sale.expectedCompletionDate).format(
                                                            "LL",
                                                        )}
                                                    </p>
                                                    {status && !status.includes("Ready") && (
                                                        <div className="mt-1">
                                                            {renderPendingDays(sale)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {status && (
                                                        <Badge
                                                            tone={STATUS_TONE[status] || "neutral"}
                                                        >
                                                            {status}
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile stacked rows */}
                        <ul className="divide-y divide-ink-100 md:hidden">
                            {upcoming.map((sale, index) => {
                                const customer = customerById(sale.customerId);
                                const status = sale.workOrder && sale.workOrder.status;
                                return (
                                    <li key={sale._id || index} className="px-4 py-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-medium text-ink-900">
                                                    {customer.firstName} {customer.lastName}
                                                </p>
                                                <p className="mt-0.5 text-xs text-ink-500">
                                                    {sale.product}
                                                </p>
                                                <p className="mt-0.5 text-xs text-ink-500">
                                                    Expected{" "}
                                                    {Moment(sale.expectedCompletionDate).format(
                                                        "ll",
                                                    )}
                                                </p>
                                                {status && !status.includes("Ready") && (
                                                    <div className="mt-1">
                                                        {renderPendingDays(sale)}
                                                    </div>
                                                )}
                                            </div>
                                            {status && (
                                                <Badge tone={STATUS_TONE[status] || "neutral"}>
                                                    {status}
                                                </Badge>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </Card>
                </>
            )}
        </div>
    );
};

export default Dashboard;
